import React, { useId, useMemo } from 'react';
import Select, { components as rsComponents } from 'react-select';
import Checkbox from 'ats/components/checkbox/Checkbox';
import {
	groupedDefaultComponents,
	purpleSelectStyles
} from 'ats/components/multi-dropdown-grouped/MultiDropdownGrouped';
import {
	ariaCheckedForState,
	buildStageFilterOptions,
	filterStageMenuOption,
	getSelectionState,
	getSelectionStateLabel,
	isStatusHeaderOption,
	toggleStatusStages
} from './stageFilterOptions';

const stageSelectStyles = {
	menu: base => ({
		...base,
		width: 'max-content',
		minWidth: 'max(100%, 260px)',
		zIndex: 20
	}),
	menuList: base => ({
		...base,
		paddingTop: 4,
		paddingBottom: 4
	}),
	group: base => ({
		...base,
		padding: 0
	}),
	option: base => ({
		...base,
		padding: 0,
		backgroundColor: 'transparent',
		color: 'inherit',
		cursor: 'pointer'
	}),
	control: purpleSelectStyles.control,
	valueContainer: base => ({
		...base,
		flexWrap: 'nowrap',
		overflow: 'hidden'
	}),
	multiValue: base => ({
		...purpleSelectStyles.multiValue(base),
		display: 'flex',
		alignItems: 'center',
		margin: '0 2px',
		maxWidth: 'none',
		minWidth: 0,
		overflow: 'visible'
	}),
	multiValueLabel: base => ({
		...purpleSelectStyles.multiValueLabel(base),
		display: 'flex',
		alignItems: 'center',
		overflow: 'visible',
		padding: '0 0 0 4px',
		whiteSpace: 'nowrap'
	}),
	multiValueRemove: purpleSelectStyles.multiValueRemove
};

function selectionStateFor(data, selected) {
	const selectedValues = (selected || []).map(option => option.value);
	if (data?.isStatusHeader) {
		return getSelectionState(data.stageValues, selectedValues);
	}
	return selectedValues.includes(data?.value) ? 'checked' : 'unchecked';
}

function StageCheckbox({ state, label }) {
	const checked = state === 'checked';
	const indeterminate = state === 'indeterminate';
	return (
		<span className="stage-filter-dropdown__checkbox">
			<span
				role="checkbox"
				aria-checked={ariaCheckedForState(state)}
				aria-label={label}>
				<Checkbox checked={checked} indeterminate={indeterminate} />
			</span>
		</span>
	);
}

function StageOption(props) {
	const { data, innerRef, innerProps, isFocused, isSelected, selectProps } =
		props;
	const selected =
		typeof props.getValue === 'function'
			? props.getValue()
			: selectProps.value || [];
	const state = data.isStatusHeader
		? selectionStateFor(data, selected)
		: isSelected
			? 'checked'
			: 'unchecked';
	const focusedClass = isFocused ? 'is-focused' : '';
	const selectedClass =
		!data.isStatusHeader && state === 'checked' ? 'is-selected' : '';
	const baseId = `${selectProps.stageFilterId}-${data.statusValue}`;

	if (data.isStatusHeader) {
		return (
			<div
				ref={innerRef}
				{...innerProps}
				className={`stage-filter-dropdown__row stage-filter-dropdown__row--status ${focusedClass}`.trim()}
				aria-selected={state === 'checked' ? 'true' : 'false'}>
				<span
					id={`${baseId}-label`}
					className="stage-filter-dropdown__status-label">
					{data.label}
				</span>
				<span id={`${baseId}-state`} className="is-sr-only">
					{getSelectionStateLabel(state)}
				</span>
				<StageCheckbox state={state} label={data.label} />
			</div>
		);
	}

	const renderStage = selectProps.renderStage;
	return (
		<div
			ref={innerRef}
			{...innerProps}
			className={`stage-filter-dropdown__row stage-filter-dropdown__row--stage ${selectedClass} ${focusedClass}`.trim()}>
			<span className="stage-filter-dropdown__stage">
				{renderStage ? (
					renderStage(data)
				) : (
					<span className="stage-filter-dropdown__stage-label">
						{data.label}
					</span>
				)}
			</span>
			<StageCheckbox state={state} label={data.label} />
		</div>
	);
}

function StatusGroup(props) {
	const { children, innerProps = {}, selectProps } = props;
	const items = React.Children.toArray(children);
	const header = items.find(child => child?.props?.data?.isStatusHeader);
	const stages = items.filter(child => child !== header);
	const statusValue = header?.props?.data?.statusValue;
	const baseId = `${selectProps.stageFilterId}-${statusValue}`;

	return (
		<div
			{...innerProps}
			role="group"
			aria-labelledby={`${baseId}-label`}
			aria-describedby={`${baseId}-state`}
			className="stage-filter-dropdown__group">
			{header}
			{stages}
			<div className="stage-filter-dropdown__divider" aria-hidden="true" />
		</div>
	);
}

function StageMultiValueLabel(props) {
	const { children, data, selectProps } = props;
	const icon = selectProps.renderValueIcon?.(data);
	const color = selectProps.getValueColor?.(data);

	return (
		<rsComponents.MultiValueLabel {...props}>
			<span className="stage-filter-dropdown__value">
				{icon ? (
					<span
						className="stage-filter-dropdown__value-icon"
						aria-hidden="true">
						{icon}
					</span>
				) : null}
				<span
					className="stage-filter-dropdown__value-label"
					style={color ? { color } : undefined}>
					{children}
				</span>
			</span>
		</rsComponents.MultiValueLabel>
	);
}

const stageSelectComponents = {
	...groupedDefaultComponents,
	MenuList: rsComponents.MenuList,
	Option: StageOption,
	Group: StatusGroup,
	GroupHeading: () => null,
	MultiValueLabel: StageMultiValueLabel
};

export default function StageFilterDropdown({
	options = [],
	value,
	onChange,
	placeholder = 'Stage',
	name = 'stage',
	groups = [],
	stageToStatus = {},
	statusGroupMap = {},
	renderStage,
	renderValueIcon,
	getValueColor
}) {
	const stageFilterId = useId();
	const selected = Array.isArray(value) ? value : [];

	const groupedOptions = useMemo(
		() =>
			buildStageFilterOptions(options, {
				groups,
				stageToStatus,
				statusGroupMap
			}),
		[options, groups, stageToStatus, statusGroupMap]
	);

	const optionsByValue = useMemo(
		() =>
			new Map(
				options
					.filter(option => option?.value)
					.map(option => [option.value, option])
			),
		[options]
	);

	const handleChange = (nextValue, meta) => {
		if (isStatusHeaderOption(meta?.option)) {
			onChange?.(
				toggleStatusStages(meta.option.stageValues, selected, optionsByValue)
			);
			return;
		}
		onChange?.(
			(nextValue || []).filter(option => !isStatusHeaderOption(option))
		);
	};

	return (
		<Select
			name={name}
			isMulti
			isClearable
			aria-label={placeholder}
			placeholder={placeholder}
			className="ats-multi-dropdown ats-multi-dropdown-grouped react-select stage-filter-dropdown"
			classNamePrefix="react-select"
			options={groupedOptions}
			value={selected}
			onChange={handleChange}
			filterOption={filterStageMenuOption}
			components={stageSelectComponents}
			styles={stageSelectStyles}
			classNames={{
				menu: () => 'stage-filter-dropdown__menu',
				menuList: () => 'stage-filter-dropdown__menu-list'
			}}
			hideSelectedOptions={false}
			closeMenuOnSelect={false}
			blurInputOnSelect={false}
			tabSelectsValue={false}
			menuPlacement="auto"
			menuPosition="fixed"
			maxMenuHeight={360}
			noOptionsMessage={() => 'No stages found'}
			stageFilterId={stageFilterId}
			renderStage={renderStage}
			renderValueIcon={renderValueIcon}
			getValueColor={getValueColor}
		/>
	);
}
